/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateMemberRequest } from '../models/create-member-request';
import { MemberDetailDto } from '../models/member-detail-dto';
import { MemberListRequest } from '../models/member-list-request';
import { OperationResultDtoOfMemberDetailDto } from '../models/operation-result-dto-of-member-detail-dto';
import { PagedResultDtoOfMemberListDto } from '../models/paged-result-dto-of-member-list-dto';
import { UpdateMemberRequest } from '../models/update-member-request';

@Injectable({
  providedIn: 'root',
})
export class MembersService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation membersListMembers
   */
  static readonly MembersListMembersPath = '/api/members/list';

  /**
   * List members.
   *
   * Returns a server-paged legacy-compatible member and shared-BAN search.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersListMembers()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  membersListMembers$Response(params: {
    body: MemberListRequest
  }): Observable<StrictHttpResponse<PagedResultDtoOfMemberListDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersListMembersPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PagedResultDtoOfMemberListDto>;
      })
    );
  }

  /**
   * List members.
   *
   * Returns a server-paged legacy-compatible member and shared-BAN search.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersListMembers$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  membersListMembers(params: {
    body: MemberListRequest
  }): Observable<PagedResultDtoOfMemberListDto> {

    return this.membersListMembers$Response(params).pipe(
      map((r: StrictHttpResponse<PagedResultDtoOfMemberListDto>) => r.body as PagedResultDtoOfMemberListDto)
    );
  }

  /**
   * Path part for operation membersGetMember
   */
  static readonly MembersGetMemberPath = '/api/members/{id}';

  /**
   * Get member.
   *
   * Returns a full member with shared BAN and OpenFire/chat state; secrets are excluded.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersGetMember()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersGetMember$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<MemberDetailDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersGetMemberPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MemberDetailDto>;
      })
    );
  }

  /**
   * Get member.
   *
   * Returns a full member with shared BAN and OpenFire/chat state; secrets are excluded.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersGetMember$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersGetMember(params: {
    id: number;
  }): Observable<MemberDetailDto> {

    return this.membersGetMember$Response(params).pipe(
      map((r: StrictHttpResponse<MemberDetailDto>) => r.body as MemberDetailDto)
    );
  }

  /**
   * Path part for operation membersUpdateMember
   */
  static readonly MembersUpdateMemberPath = '/api/members/{id}';

  /**
   * Update member.
   *
   * Replaces editable member and shared-BAN values and reports side-effect warnings.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersUpdateMember()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  membersUpdateMember$Response(params: {
    id: number;
    body: UpdateMemberRequest
  }): Observable<StrictHttpResponse<OperationResultDtoOfMemberDetailDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersUpdateMemberPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfMemberDetailDto>;
      })
    );
  }

  /**
   * Update member.
   *
   * Replaces editable member and shared-BAN values and reports side-effect warnings.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersUpdateMember$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  membersUpdateMember(params: {
    id: number;
    body: UpdateMemberRequest
  }): Observable<OperationResultDtoOfMemberDetailDto> {

    return this.membersUpdateMember$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfMemberDetailDto>) => r.body as OperationResultDtoOfMemberDetailDto)
    );
  }

  /**
   * Path part for operation membersDeleteMember
   */
  static readonly MembersDeleteMemberPath = '/api/members/{id}';

  /**
   * Soft-delete member.
   *
   * Applies the same forced security transition as saving Deleted=true.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersDeleteMember()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersDeleteMember$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<OperationResultDtoOfMemberDetailDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersDeleteMemberPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfMemberDetailDto>;
      })
    );
  }

  /**
   * Soft-delete member.
   *
   * Applies the same forced security transition as saving Deleted=true.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersDeleteMember$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersDeleteMember(params: {
    id: number;
  }): Observable<OperationResultDtoOfMemberDetailDto> {

    return this.membersDeleteMember$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfMemberDetailDto>) => r.body as OperationResultDtoOfMemberDetailDto)
    );
  }

  /**
   * Path part for operation membersCreateMember
   */
  static readonly MembersCreateMemberPath = '/api/members';

  /**
   * Create member.
   *
   * Creates the member aggregate transactionally and reports post-commit side-effect warnings.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersCreateMember()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  membersCreateMember$Response(params: {
    body: CreateMemberRequest
  }): Observable<StrictHttpResponse<OperationResultDtoOfMemberDetailDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersCreateMemberPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfMemberDetailDto>;
      })
    );
  }

  /**
   * Create member.
   *
   * Creates the member aggregate transactionally and reports post-commit side-effect warnings.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersCreateMember$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  membersCreateMember(params: {
    body: CreateMemberRequest
  }): Observable<OperationResultDtoOfMemberDetailDto> {

    return this.membersCreateMember$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfMemberDetailDto>) => r.body as OperationResultDtoOfMemberDetailDto)
    );
  }

  /**
   * Path part for operation membersClearRegistration
   */
  static readonly MembersClearRegistrationPath = '/api/members/{id}/clear-registration';

  /**
   * Clear member registration.
   *
   * Clears the member device and registration IDs, disables the device, unblocks the member, and invalidates member caches.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersClearRegistration()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersClearRegistration$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<OperationResultDtoOfMemberDetailDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersClearRegistrationPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfMemberDetailDto>;
      })
    );
  }

  /**
   * Clear member registration.
   *
   * Clears the member device and registration IDs, disables the device, unblocks the member, and invalidates member caches.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersClearRegistration$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersClearRegistration(params: {
    id: number;
  }): Observable<OperationResultDtoOfMemberDetailDto> {

    return this.membersClearRegistration$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfMemberDetailDto>) => r.body as OperationResultDtoOfMemberDetailDto)
    );
  }

  /**
   * Path part for operation membersRetrySideEffects
   */
  static readonly MembersRetrySideEffectsPath = '/api/members/{id}/retry-side-effects';

  /**
   * Retry member side effects.
   *
   * Retries idempotent cache, OpenFire, mobile, and compatibility reconciliation without changing member data.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `membersRetrySideEffects()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersRetrySideEffects$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<OperationResultDtoOfMemberDetailDto>> {

    const rb = new RequestBuilder(this.rootUrl, MembersService.MembersRetrySideEffectsPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfMemberDetailDto>;
      })
    );
  }

  /**
   * Retry member side effects.
   *
   * Retries idempotent cache, OpenFire, mobile, and compatibility reconciliation without changing member data.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `membersRetrySideEffects$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  membersRetrySideEffects(params: {
    id: number;
  }): Observable<OperationResultDtoOfMemberDetailDto> {

    return this.membersRetrySideEffects$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfMemberDetailDto>) => r.body as OperationResultDtoOfMemberDetailDto)
    );
  }

}
